import { ALLOCATION_DIMENSION } from "constants/action-types";
import { Observable } from "rxjs";
import { ActionsObservable } from "redux-observable";
import { ajax } from "lib/observable";
import siteConfig from "site-config";
import {
    ListAllocationDimensionRequestAction,
    listAllocationDimensionComplete,
    GetAllocationDimensionRequestAction,
    getAllocationDimensionComplete,
    CreateAllocationDimensionCompleteAction,
    createAllocationDimensionComplete,
    UpdateAllocationDimensionRequestAction,
    updateAllocationDimensionComplete,
    createAllocationDimensionRequest,
    CreateAllocationDimensionRequestAction,
} from "actions/allocation-dimension";
import { updateMerchantPreferenceRequest } from "actions/merchant";
import { formatMessage } from "lib/globalize-util";
import {
    AllocationDimensionOwnerObject,
    buildMutateOperationFromAllocationDimension,
    defaultAllocationDimensions,
    IAllocationDimension,
} from "helpers/allocation-dimension";
import {
    MERCHANT_ALLOCATION_DIMENSION_ID_PREFIX,
    COLLECTION_MERCHANT_PREFERENCE_NAMESPACE,
} from "constants/product-collection";
import { add as snackbarAdd } from "actions/snackbars";
import { MutableType } from "actions/mutate";

const createDefaultDimensionsObservables = (allocationDimensions, objectId, owner, merchantV0Uuid) =>
    allocationDimensions.map((dimension) =>
        createAllocationDimension(
            ActionsObservable.of(createAllocationDimensionRequest(dimension, objectId, owner, merchantV0Uuid))
        )
    );

export const listAllocationDimensions = (action$: ActionsObservable<ListAllocationDimensionRequestAction>) =>
    action$
        .ofType(ALLOCATION_DIMENSION.LIST.REQUEST)
        .map(({ payload }) => payload)
        .switchMap(({ objectId, owner }) => {
            let path;
            switch (owner) {
                case AllocationDimensionOwnerObject.Merchant:
                    path = `${siteConfig.goAPI}/v1/collections/merchants/${objectId}/allocation_dimensions`;
                    break;
                default:
                    return [listAllocationDimensionComplete(new Error(`Unknown owner type: ${owner}`))];
            }

            return ajax(path)
                .flatMap(({ response, status }) => {
                    if (status !== 200) {
                        throw new Error(response.message || formatMessage("Unknown fetch error"));
                    }
                    return [listAllocationDimensionComplete(response.data)];
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of(listAllocationDimensionComplete(error)),
                        Observable.of(
                            snackbarAdd({
                                message: error.message || formatMessage("Error getting allocation dimensions"),
                            })
                        )
                    )
                );
        });

export const createAllocationDimension = (action$: ActionsObservable<CreateAllocationDimensionRequestAction>) =>
    action$.ofType(ALLOCATION_DIMENSION.CREATE.REQUEST).switchMap(({ payload, meta }) => {
        const { allocationDimension } = payload;
        const { objectId, owner, merchantV0Uuid } = meta;
        let path;
        switch (owner) {
            case AllocationDimensionOwnerObject.Merchant:
                path = `${siteConfig.goAPI}/v1/collections/merchants/${objectId}/allocation_dimensions`;
                break;
            default:
                return [
                    createAllocationDimensionComplete(
                        new Error(`Unknown owner type: ${owner}`),
                        objectId,
                        owner,
                        merchantV0Uuid
                    ),
                ];
        }

        return ajax({
            method: "POST",
            url: path,
            body: {
                allocation_dimension: allocationDimension,
            },
        })
            .flatMap(({ response, status }) => {
                if (status !== 200) {
                    throw new Error(response.message || formatMessage("Unknown create error"));
                }
                return [
                    createAllocationDimensionComplete(response, objectId, owner, merchantV0Uuid),
                    snackbarAdd({ message: formatMessage(`Merchant allocation dimension: ${response.name} created`) }),
                ];
            })
            .catch((error) =>
                Observable.merge(
                    Observable.of(createAllocationDimensionComplete(error, objectId, owner, merchantV0Uuid)),
                    Observable.of(
                        snackbarAdd({ message: error.message || formatMessage("Error creating allocation dimension") })
                    )
                )
            );
    });

export const updateAllocationDimension = (action$: ActionsObservable<UpdateAllocationDimensionRequestAction>) =>
    action$.ofType(ALLOCATION_DIMENSION.UPDATE.REQUEST).switchMap(({ payload, meta }) => {
        const { allocationDimensionId, allocationDimension } = payload;
        const { objectId, owner } = meta;
        let path;
        switch (owner) {
            case AllocationDimensionOwnerObject.Merchant:
                path = `${siteConfig.goAPI}/v1/collections/merchants/${objectId}/allocation_dimensions/${allocationDimensionId}`;
                break;
            default:
                return [updateAllocationDimensionComplete(new Error(`Unknown owner type: ${owner}`), objectId, owner)];
        }
        const operations = buildMutateOperationFromAllocationDimension(allocationDimension);

        return ajax({
            method: "PUT",
            url: path,
            body: {
                operations,
            },
        })
            .flatMap(({ response, status }) => {
                if (status !== 200) {
                    throw new Error(response.message || formatMessage("Unknown update error"));
                }

                return [
                    updateAllocationDimensionComplete(response, objectId, owner),
                    snackbarAdd({ message: formatMessage(`Merchant allocation dimension: ${response.name} updated`) }),
                ];
            })
            .catch((error) =>
                Observable.merge(
                    Observable.of(updateAllocationDimensionComplete(error, objectId, owner)),
                    Observable.of(
                        snackbarAdd({ message: error.message || formatMessage("Error updating allocation dimension") })
                    )
                )
            );
    });

// Update merchant preference to use new revision.
// Add allocation dimension if it wasn't inside merchant preference.
export const syncWithMerchantPreference = (action$: ActionsObservable<CreateAllocationDimensionCompleteAction>) =>
    action$
        .ofType(ALLOCATION_DIMENSION.CREATE.COMPLETE)
        .filter(({ meta, payload }) => {
            return !(payload instanceof Error) && meta.owner === AllocationDimensionOwnerObject.Merchant;
        })
        .switchMap(({ payload, meta }) => {
            const { objectId, merchantV0Uuid } = meta;
            return ajax(`${siteConfig.goAPI}/v1/collections/merchants/${objectId}/preference`)
                .switchMap(({ response, status }) => {
                    if (status !== 200) {
                        return Observable.of(null);
                    }

                    return Observable.of(response);
                })
                .filter((merchantPreference) => !!merchantPreference)
                .switchMap((merchantPreference) => {
                    const allocationDimension = payload as IAllocationDimension;
                    let isInExisting = false;
                    const updatedDefaultAllocationDimension = {
                        namespace: COLLECTION_MERCHANT_PREFERENCE_NAMESPACE,
                        document_id: merchantV0Uuid,
                        object_id: `${MERCHANT_ALLOCATION_DIMENSION_ID_PREFIX}${allocationDimension.id}`,
                        revision: 0,
                        branch: "",
                        type: MutableType.Mixed,
                    };
                    const updatedDefaultAllocationDimensions = [];
                    merchantPreference.default_allocation_dimensions.forEach((defaultAllocationDimension) => {
                        if (
                            defaultAllocationDimension.object_id.replace(
                                MERCHANT_ALLOCATION_DIMENSION_ID_PREFIX,
                                ""
                            ) === allocationDimension.id
                        ) {
                            isInExisting = true;
                            updatedDefaultAllocationDimensions.push(updatedDefaultAllocationDimension);
                        } else {
                            updatedDefaultAllocationDimensions.push(defaultAllocationDimension);
                        }
                    });

                    if (!isInExisting) {
                        updatedDefaultAllocationDimensions.push(updatedDefaultAllocationDimension);
                    }
                    return [
                        updateMerchantPreferenceRequest(objectId, {
                            default_allocation_dimensions: updatedDefaultAllocationDimensions,
                        }),
                    ];
                });
        });

export const getAllocationDimension = (action$: ActionsObservable<GetAllocationDimensionRequestAction>, store$) =>
    action$.ofType(ALLOCATION_DIMENSION.GET.REQUEST).switchMap(({ payload, meta }) => {
        const state = store$.getState();
        const { collection } = state;
        const { objectId, owner } = meta;
        let path;
        switch (owner) {
            case AllocationDimensionOwnerObject.Merchant:
                path = `${siteConfig.goAPI}/v1/collections/merchants/${objectId}/allocation_dimensions/${payload}`;
                break;
            case AllocationDimensionOwnerObject.Collection:
                path = `${siteConfig.goAPI}/v1/collections/${objectId}/allocation_dimensions/${payload}?merchant_id=${collection.merchantId}`;
                break;
            default:
                return [getAllocationDimensionComplete(new Error(`Unknown owner type: ${owner}`), objectId, owner)];
        }

        return ajax(path)
            .flatMap(({ response, status }) => {
                if (status !== 200) {
                    throw new Error(response.message || formatMessage("Unknown fetch error"));
                }
                return [getAllocationDimensionComplete(response, objectId, owner)];
            })
            .catch((error) =>
                Observable.merge(
                    Observable.of(getAllocationDimensionComplete(error, objectId, owner)),
                    Observable.of(
                        snackbarAdd({ message: error.message || formatMessage("Error getting allocation dimension") })
                    )
                )
            );
    });

export const createDefaultAllocationDimensions = (action$) => {
    return action$.ofType(ALLOCATION_DIMENSION.CREATE_DEFAULT.REQUEST).concatMap(({ meta }) => {
        const { objectId, owner, merchantV0Uuid } = meta;

        const createDimensions$ = createDefaultDimensionsObservables(
            defaultAllocationDimensions,
            objectId,
            owner,
            merchantV0Uuid
        );

        return Observable.concat(...createDimensions$)
            .catch((error) => {
                return Observable.merge(
                    Observable.of(
                        snackbarAdd({
                            message: formatMessage(
                                "Couldn't create all allocation dimensions. Failed at: |dimensionName|",
                                {
                                    dimensionName: error.allocationDimension?.name,
                                }
                            ),
                        })
                    ),

                    Observable.of(createAllocationDimensionComplete(error, objectId, owner, merchantV0Uuid))
                );
            })
            .filter((action: any) => action.type === ALLOCATION_DIMENSION.CREATE.COMPLETE && !action.error)
            .concat(
                Observable.of(
                    snackbarAdd({ message: formatMessage("All allocation dimensions have been created successfully") })
                )
            );
    });
};
